type ShiftId = number | string;

type ShiftLike = { id?: ShiftId; shift_id?: ShiftId };

export type ShiftPatch = Record<string, unknown>;

export type PendingShiftPatches = Map<ShiftId, { patch: ShiftPatch; seq: number }>;

const getShiftId = (shift: ShiftLike) => String(shift.shift_id ?? shift.id ?? '');

/**
 * Returns a new list with the matching shift merged with `patch`. Untouched shifts keep their
 * references so memoized calendar data downstream only changes where it has to.
 */
export const applyShiftPatch = <T extends ShiftLike>(
	shifts: T[],
	id: ShiftId,
	patch: ShiftPatch
): T[] => {
	const targetId = String(id);
	let didPatch = false;

	const next = shifts.map((shift) => {
		if (getShiftId(shift) !== targetId) return shift;
		didPatch = true;
		return { ...shift, ...patch };
	});

	return didPatch ? next : shifts;
};

/**
 * Re-applies drag updates whose PUT has not settled yet on top of freshly fetched shifts, so a
 * response that was generated before the PUT landed can't move the shift back.
 */
export const overlayPendingShiftPatches = <T extends ShiftLike>(
	shifts: T[],
	pending: PendingShiftPatches
): T[] => {
	if (pending.size === 0) return shifts;

	let result = shifts;
	pending.forEach(({ patch }, id) => {
		result = applyShiftPatch(result, id, patch);
	});
	return result;
};
